<template>
  <w-text-field
    v-if="!readonly"
    ref="restrictedTextField"
    v-model="internalValue"
    class="w-restricted-text-field"
    v-bind="$props"
    :next-to-each-other="nextToEachOther"
    :rules="internalRules"
    :placeholder="placeholder"
    @focus="fieldHasFocus = true"
    @blur="_onBlur"
    @focusin="_onFocusIn"
    @focusout="_onFocusOut"
    @keydown="_onKeyDown"
    @keyup="_onKeyUp"
    @click:clear="$emit('input', null)"
    @click:append="$emit('click:append')"
    @change="$emit('change', $event)"
  >
    <template #prepend>
      <slot name="prepend" />
    </template>

    <template #prepend-inner>
      <slot name="prepend-inner" />
    </template>

    <template #append>
      <slot name="append" />
    </template>

    <template #append-outer>
      <slot name="append-outer" />
    </template>
  </w-text-field>

  <w-read-only-text-field
    v-else
    v-bind="$props"
    :value="readOnlyFormatter(value)"
    :next-to-each-other="nextToEachOther"
    @click="$emit('click', $event)"
  />
</template>

<script>
// components
import WorkTextField from '@/components/global_components/forms/WorkTextField';

// mixins
import isRequired from '@/mixins/component/is-required-mixin';
import pendingAble from '@/mixins/component/pending-able-mixin';

export default {
  name: 'WorkBaseRestrictedTextField',
  mixins: [isRequired, pendingAble],
  props: {
    ...WorkTextField.props,
    // model
    value: { type: [String, Number] },
    // config
    charWhitelist: { type: Array, required: true },
    alwaysAllowedKeys: { type: Array, required: true },
    arrayOfIsValidFunctions: { type: Array, default: () => [] },
    inputGetFormatter: { type: Function, default: (v) => v },
    inputSetFormatter: { type: Function, default: (v) => v },
    readOnlyFormatter: { type: Function, default: (v) => v },
    formatValueOnFocusIn: { type: Function, required: false },
    formatValueOnFocusOut: { type: Function, required: false },
    withDecreaseIncreaseButtons: { type: Boolean, default: false },
    // flags
    alignRight: { type: Boolean, default: false },
    nextToEachOther: { type: Boolean, default: false },
    rulesCallback: { type: Function, required: false },
    placeholder: { type: String, required: false },
    spaceBetween: { type: Boolean, default: false }
  },
  data() {
    return {
      // flags
      fieldHasFocus: false,
      // values
      internalValue: null,
      internalLazyValue: null,
      lazyExternalValue: null,
      // cursor
      lastCursorPosStart: 0,
      lastCursorPosEnd: 0,
    };
  },
  computed: {
    _externalValue: {
      get() {
        return this.value;
      },
      set(value) {
        if (value == undefined || value.length === 0) {
          return this.$emit('input', null);
        }

        const valueFormatted = this.inputSetFormatter(value);
        this.lazyExternalValue = valueFormatted;
        this.$emit('input', valueFormatted);
      },
    },
    internalRules() {
      if (this.rulesCallback) {
        return this.rulesCallback(this.internalValue);
      }
      return this.rules;
    },
  },
  watch: {
    value: {
      handler(newValue) {
        // only set internal value, if value changed from outside
        if (newValue === this.lazyExternalValue && !this.withDecreaseIncreaseButtons) {
          return;
        }
        if (newValue == undefined) {
          return this.updateInternalValues(newValue);
        }

        let value = undefined;
        if (
          this.fieldHasFocus === true &&
          this.formatValueOnFocusIn != undefined
        ) {
          value = this.formatValueOnFocusIn(newValue);
        } else if (
          this.fieldHasFocus === false &&
          this.formatValueOnFocusOut != undefined
        ) {
          value = this.formatValueOnFocusOut(newValue);
        } else {
          value = this.inputGetFormatter(newValue);
        }

        this.lazyExternalValue = this.inputSetFormatter(value);
        this.internalValue = value;
      },
      immediate: true,
    },
  },
  methods: {
    _onKeyDown(event) {
      if (this.alwaysAllowedKeys.includes(event.key)) {
        return;
      }
      if (this.charWhitelist.includes(event.key) === false) {
        event.preventDefault();
      }
    },
    async _onKeyUp(event) {
      if (this._isValueValidInput(event.target.value)) {
        this.internalLazyValue = event.target.value;
        this._externalValue = event.target.value;
      } else {
        await this._saveCurrentCursorPosition();
        this.internalValue = this.internalLazyValue;
        this._placeCursorAtLastPosition();
      }
    },
    _isValueValidInput(value) {
      for (const valueIsValidFunc of this.arrayOfIsValidFunctions) {
        if (valueIsValidFunc(value) === false) {
          return false;
        }
      }
      return true;
    },
    _onFocusIn() {
      if (
        this.formatValueOnFocusIn != undefined &&
        this._externalValue != undefined
      ) {
        this.updateInternalValues(
          this.formatValueOnFocusIn(this._externalValue)
        );
      }
    },
    _onFocusOut() {
      if (
        this.formatValueOnFocusOut != undefined &&
        this._externalValue != undefined
      ) {
        this.updateInternalValues(
          this.formatValueOnFocusOut(this._externalValue)
        );
      }
    },
    _onBlur(event) {
      this.fieldHasFocus = false;
      this.$emit('blur', event);
    },
    /*********************************
     * ### HELPERS ###
     *********************************/
    _saveCurrentCursorPosition() {
      this.lastCursorPosStart =
        this.$refs.restrictedTextField.$refs.input.selectionStart;
      this.lastCursorPosEnd =
        this.$refs.restrictedTextField.$refs.input.selectionEnd;
    },
    _placeCursorAtLastPosition() {
      this.$nextTick(() => {
        this.$refs.restrictedTextField.$refs.input.selectionStart =
          this.lastCursorPosStart - 1;
        this.$refs.restrictedTextField.$refs.input.selectionEnd =
          this.lastCursorPosEnd - 1;
      });
    },
    focus() {
      this.$refs.restrictedTextField.focus();
    },
    /******************************************************
     * ### ALSO CALLABLE FROM WRAPPER, IF UPDATE NEEDED ###
     * e.g. formatter changed
     *****************************************************/
    updateInternalValues(value) {
      this.internalValue = value;
      this.internalLazyValue = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.w-restricted-text-field ::v-deep .v-input__append-inner {
  margin: auto;
}
.w-restricted-text-field.w-text-field--input > ::v-deep.v-input__control {
  > .v-input__slot {
    background: $input-bg-color !important;
  } > * {
      font-size: 1rem;
    }
}
</style>
